
import React, { useState, useEffect } from 'react';
import { storage } from '../lib/storage';
import { taxService } from '../services/taxService';
import { Order, Store, TaxReport } from '../types';
import { 
  FileText, 
  Download, 
  Search, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  Info, 
  Filter, 
  Printer, 
  ShieldCheck, 
  ArrowUpRight,
  ChevronRight,
  MoreVertical,
  Scale
} from 'lucide-react';

interface AdminInvoicesProps {
  storeId: string;
}

const AdminInvoices: React.FC<AdminInvoicesProps> = ({ storeId }) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [store, setStore] = useState<Store>(storage.getStoreConfig(storeId));
  const [report, setReport] = useState<TaxReport | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('This Month');

  useEffect(() => {
    const storeOrders = storage.getOrders().filter(o => o.storeId === storeId);
    setOrders(storeOrders);
    setReport(taxService.generateTaxSummary(storeOrders));
    setStore(storage.getStoreConfig(storeId));
  }, [storeId]);

  const filteredOrders = orders.filter(o => 
    o.id.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
           <h1 className="text-3xl font-black text-gray-900 tracking-tight">Taxation & Invoicing</h1>
           <p className="text-gray-400 text-sm font-bold uppercase tracking-widest">Compliance Control Center</p>
        </div>
        <div className="flex gap-4">
           <div className="flex bg-white rounded-2xl p-1 shadow-sm border border-gray-100">
             {['This Month', 'Last Quarter', 'FY 24-25'].map(p => (
               <button 
                 key={p} 
                 onClick={() => setSelectedPeriod(p)}
                 className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${selectedPeriod === p ? 'bg-blue-600 text-white shadow-lg' : 'text-gray-400 hover:text-gray-700'}`}
               >
                 {p}
               </button>
             ))}
           </div>
           <button className="flex items-center gap-2 bg-gray-900 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-black transition-all">
              <Download className="h-4 w-4" /> Export Report
           </button>
        </div>
      </div>

      {!store.taxConfig?.enabled && (
        <div className="bg-amber-50 border-2 border-amber-100 p-8 rounded-[2.5rem] flex items-center justify-between gap-6">
           <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-amber-500 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-amber-200">
                 <AlertCircle className="h-8 w-8" />
              </div>
              <div>
                 <h3 className="text-xl font-black text-amber-900">Tax Setup Incomplete</h3>
                 <p className="text-sm text-amber-700 font-medium">Bhai, aapne abhi tak tax configuration enable nahi kiya hai. Proper invoices generate karne ke liye settings mein GSTIN/VAT details bhar dein.</p>
              </div>
           </div>
           <button className="bg-amber-900 text-white px-8 py-4 rounded-2xl font-black text-xs uppercase tracking-widest">Update Settings</button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4 group">
            <div className="flex justify-between items-start">
               <div className="p-4 bg-blue-50 text-blue-600 rounded-2xl group-hover:rotate-6 transition-transform">
                  <Scale className="h-6 w-6" />
               </div>
               <span className="text-[10px] font-black text-blue-600 bg-blue-50 px-2 py-1 rounded-full uppercase">Taxable</span>
            </div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Gross Taxable Value</p>
               <h3 className="text-4xl font-black text-gray-900">${report?.totalTaxableValue.toLocaleString()}</h3>
            </div>
         </div>
         <div className="bg-white p-10 rounded-[40px] border border-gray-100 shadow-xl space-y-4 group">
            <div className="flex justify-between items-start">
               <div className="p-4 bg-emerald-50 text-emerald-600 rounded-2xl group-hover:rotate-6 transition-transform">
                  <ShieldCheck className="h-6 w-6" />
               </div>
               <span className="text-[10px] font-black text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full uppercase">Collected</span>
            </div>
            <div>
               <p className="text-gray-400 text-[10px] font-black uppercase tracking-widest mb-1">Total Tax (Output)</p>
               <h3 className="text-4xl font-black text-emerald-600">${report?.totalTaxCollected.toLocaleString()}</h3>
            </div>
         </div>
         <div className="bg-gray-900 p-10 rounded-[40px] text-white shadow-2xl space-y-4 group relative overflow-hidden">
            <div className="absolute top-0 right-0 p-8 opacity-10"><Info className="h-20 w-20" /></div>
            <div className="relative">
               <div className="p-4 bg-white/10 backdrop-blur-xl rounded-2xl w-fit mb-4">
                  <FileText className="h-6 w-6 text-blue-400" />
               </div>
               <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Compliance Status</p>
               <h3 className="text-2xl font-black">{store.taxConfig?.enabled ? 'Fully Compliant' : 'Self-Declaration'}</h3>
               <p className="text-[10px] text-blue-400 font-bold uppercase mt-2">Verified by Lynqar Compliance Engine</p>
            </div>
         </div>
      </div>

      <div className="bg-white rounded-[40px] border border-gray-100 shadow-xl overflow-hidden">
         <div className="p-10 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
               <FileText className="h-6 w-6 text-blue-600" />
               <h3 className="text-2xl font-black text-gray-900 tracking-tight">Invoice History</h3>
            </div>
            <div className="relative w-full md:w-80">
               <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-300" />
               <input 
                 type="text" 
                 placeholder="Search by Order ID..." 
                 value={searchQuery}
                 onChange={e => setSearchQuery(e.target.value)}
                 className="w-full pl-12 pr-4 py-4 bg-gray-50 border-none rounded-2xl font-bold text-sm focus:ring-2 focus:ring-blue-500"
               />
            </div>
         </div>
         <div className="overflow-x-auto">
            <table className="w-full text-left">
               <thead className="bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest border-b border-gray-100">
                  <tr>
                     <th className="px-10 py-6">Order ID</th>
                     <th className="px-10 py-6">Date</th>
                     <th className="px-10 py-6">Taxable Value</th>
                     <th className="px-10 py-6">Tax Amount</th>
                     <th className="px-10 py-6">Status</th>
                     <th className="px-10 py-6 text-right">Actions</th>
                  </tr>
               </thead>
               <tbody className="divide-y divide-gray-50">
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-10 py-32 text-center text-gray-400 font-bold uppercase tracking-widest text-xs opacity-50">No invoices found for this criteria.</td>
                    </tr>
                  ) : (
                    filteredOrders.map(order => (
                      <tr key={order.id} className="group hover:bg-gray-50/50 transition-all">
                        <td className="px-10 py-8">
                           <p className="font-black text-sm text-gray-900">#{order.id}</p>
                        </td>
                        <td className="px-10 py-8">
                           <p className="text-xs font-bold text-gray-500">{new Date(order.createdAt).toLocaleDateString()}</p>
                        </td>
                        <td className="px-10 py-8 font-bold text-gray-700">${order.subtotal.toLocaleString()}</td>
                        <td className="px-10 py-8">
                           <p className="font-black text-emerald-600">${order.taxAmount.toLocaleString()}</p>
                           <p className="text-[8px] text-gray-400 font-black uppercase">{order.taxRate}% Rate</p>
                        </td>
                        <td className="px-10 py-8">
                           <span className={`px-4 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${
                             order.status === 'PAID' || order.status === 'DELIVERED' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-gray-50 text-gray-400 border-gray-100'
                           }`}>
                             {order.status === 'PAID' || order.status === 'DELIVERED' ? 'Generated' : 'Pending'}
                           </span>
                        </td>
                        <td className="px-10 py-8 text-right">
                           <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button className="p-3 text-blue-600 hover:bg-blue-50 rounded-xl transition-all"><Download className="h-4 w-4" /></button>
                              <button className="p-3 text-gray-400 hover:bg-gray-100 rounded-xl transition-all"><Printer className="h-4 w-4" /></button>
                           </div>
                        </td>
                      </tr>
                    ))
                  )}
               </tbody>
            </table>
         </div>
      </div>
    </div>
  );
};

export default AdminInvoices;
